import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FileText, Hourglass, Inbox, Receipt } from "lucide-react";
import { useChartTheme } from "@/shared/hooks/useChartTheme";
import { getAuthUser } from "../auth";
import { fetchClaimsReport, fetchDecisionDashboard, type ClaimsReport, type DecisionDashboard } from "../api/claimsApi";
import { fetchScopeCompanies, type ScopeCompany } from "../api/insurerApi";
import { openPaymentNote } from "../api/invoicePrintApi";
import { DashboardBanner } from "../components/insurer-dashboard/DashboardBanner";
import { InsurerKpiGrid } from "../components/insurer-dashboard/InsurerKpiGrid";
import { InsurerPipeline } from "../components/insurer-dashboard/InsurerPipeline";

function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function percent(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
}

function shortDate(value: string | null): string {
  return value ? new Date(value.replace(" ", "T")).toLocaleDateString("fr-FR") : "—";
}

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

function axisAmount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M`;
  if (value >= 1_000) return `${Math.round(value / 1_000).toLocaleString("fr-FR")} k`;
  return value.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
}

function MonthlyTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip px-3 py-2 text-foreground">
      {label ? <p className="mb-1 text-xs text-muted">{label}</p> : null}
      {payload.map((entry) => (
        <p key={entry.name} className="text-xs font-medium tabular-nums" style={{ color: entry.color }}>
          {entry.name} : {money(Number(entry.value))} USD
        </p>
      ))}
    </div>
  );
}

function MonthlyEvolution({ months }: { months: Array<{ month: string; billed: number; validated: number }> }) {
  const { tick, grid, isDark } = useChartTheme();
  const billed = isDark ? "#94a3b8" : "#64748b";
  const validated = isDark ? "#34d399" : "#059669";
  const data = months.map((month, index) => ({
    label: MONTHS[index] ?? month.month,
    billed: month.billed,
    validated: month.validated,
  }));
  const peak = Math.max(0, ...data.flatMap((row) => [row.billed, row.validated]));
  const empty = peak === 0;

  return (
    <section className="theme-card p-5 xl:col-span-2">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">
          Évolution mensuelle : <span className="font-medium text-slate-500 dark:text-slate-400">facturé</span>
          {" et "}
          <span className="font-medium text-emerald-600 dark:text-emerald-400">validé</span>
        </h2>
        <p className="flex gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-slate-500 dark:bg-slate-400" aria-hidden="true" />
            Facturé
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-emerald-600 dark:bg-emerald-400" aria-hidden="true" />
            Validé
          </span>
        </p>
      </div>
      <div className="h-56" role="img" aria-label="Évolution mensuelle des montants facturés et validés">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barGap={3} barCategoryGap="22%" margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: tick, fontSize: 12 }} interval={0} />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={44}
              tick={{ fill: tick, fontSize: 11 }}
              tickFormatter={axisAmount}
              domain={empty ? [0, 1] : [0, "auto"]}
              allowDecimals={false}
            />
            <Tooltip content={<MonthlyTooltip />} cursor={{ fill: "rgb(148 163 184 / 0.12)" }} />
            <Bar dataKey="billed" name="Facturé" fill={billed} radius={[5, 5, 0, 0]} maxBarSize={16} />
            <Bar dataKey="validated" name="Validé" fill={validated} radius={[5, 5, 0, 0]} maxBarSize={16} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {empty ? <p className="mt-2 text-sm text-muted-foreground">Aucune facture facturée ni validée sur cet exercice.</p> : null}
    </section>
  );
}

const PROVIDER_PERIODS = [
  { value: "toutes", label: "Toutes" },
  { value: "annuel", label: "Annuel" },
  { value: "semestriel", label: "Semestriel" },
  { value: "trimestriel", label: "Trimestriel" },
  { value: "mensuel", label: "Mensuel" },
] as const;

type ProviderPeriod = (typeof PROVIDER_PERIODS)[number]["value"];

function dateStamp(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Fenêtre de la carte Prestataires, bornée à l'année choisie. */
function providerRange(period: ProviderPeriod, year: number, today: Date): { from: string; to: string } {
  const anchor = year === today.getFullYear() ? today : new Date(year, 11, 31);
  const yearEnd = `${year}-12-31`;
  const to = period === "annuel" || year !== today.getFullYear() ? yearEnd : today.toISOString().slice(0, 10);
  if (period === "toutes" || period === "annuel") return { from: `${year}-01-01`, to };
  const month = anchor.getMonth();
  if (period === "semestriel") return { from: dateStamp(year, month < 6 ? 0 : 6, 1), to };
  if (period === "trimestriel") return { from: dateStamp(year, Math.floor(month / 3) * 3, 1), to };
  return { from: dateStamp(year, month, 1), to };
}

/** Part de la dépense validée : élevé, moyen, puis faible. */
function shareBarClass(share: number): string {
  if (share >= 40) return "bg-red-500 dark:bg-red-400";
  if (share >= 15) return "bg-amber-500 dark:bg-amber-400";
  return "bg-emerald-500 dark:bg-emerald-400";
}

function TopList({
  title,
  rows,
  href,
  companyFilter,
  periodFilter,
}: {
  title: string;
  rows: Array<{ label: string; validated: number; share: number }>;
  href?: string;
  companyFilter?: {
    id: string;
    value: string;
    companies: ScopeCompany[];
    onChange: (value: string) => void;
  };
  periodFilter?: {
    id: string;
    value: ProviderPeriod;
    onChange: (value: ProviderPeriod) => void;
  };
}) {
  return (
    <section className="theme-card relative p-5 transition-colors hover:border-accent">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <div className="flex items-center gap-2">
          {companyFilter ? (
            <select
              id={companyFilter.id}
              value={companyFilter.value}
              onChange={(event) => companyFilter.onChange(event.target.value)}
              aria-label="Filtrer par entreprise"
              className="relative z-10 h-8 max-w-[11rem] rounded-lg border border-border bg-card px-2 text-xs text-foreground"
            >
              <option value="">Tous</option>
              {companyFilter.companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          ) : periodFilter ? (
            <select
              id={periodFilter.id}
              value={periodFilter.value}
              onChange={(event) => periodFilter.onChange(event.target.value as ProviderPeriod)}
              aria-label="Filtrer les prestataires par période"
              className="relative z-10 h-8 rounded-lg border border-border bg-card px-2 text-xs text-foreground"
            >
              {PROVIDER_PERIODS.map((period) => (
                <option key={period.value} value={period.value}>{period.label}</option>
              ))}
            </select>
          ) : null}
          {/* Le lien couvre toute la carte ; les filtres restent au-dessus. */}
          {href ? <Link to={href} className="text-sm text-primary after:absolute after:inset-0 after:content-['']">Détail</Link> : null}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted">Aucune dépense validée sur la période.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.slice(0, 5).map((row) => (
            <li key={row.label} className="text-sm">
              <div className="flex justify-between gap-2">
                <span className="truncate text-foreground">{row.label}</span>
                <span className="tabular-nums text-muted">{money(row.validated)}</span>
              </div>
              <span className="mt-1 block h-1.5 rounded-full bg-card-muted">
                <span
                  className={`block h-1.5 rounded-full ${shareBarClass(row.share)}`}
                  style={{ width: `${Math.max(1, row.share)}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Compte Assurance : tableau de bord décisionnel de l'année. Avancement du traitement des factures,
 * montants à payer et économies, notes de paiement, tendance, entreprises, demandes de validation,
 * et dépenses par nature, prestataire et affection (rapport de sinistralité, sans nom).
 */
export function InsurerDashboardPage() {
  const thisYear = new Date().getFullYear();
  const year = thisYear;
  const [dashboard, setDashboard] = useState<DecisionDashboard | null>(null);
  const [report, setReport] = useState<ClaimsReport | null>(null);
  const [companies, setCompanies] = useState<ScopeCompany[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [providerPeriod, setProviderPeriod] = useState<ProviderPeriod>("toutes");
  const [providerRows, setProviderRows] = useState<ClaimsReport["by_provider"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const insurerName = getAuthUser()?.name ?? "Assureur";

  useEffect(() => {
    fetchScopeCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchDecisionDashboard(year)
      .then((decision) => {
        if (!cancelled) setDashboard(decision);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Tableau de bord indisponible.");
      });
    return () => {
      cancelled = true;
    };
  }, [year]);

  useEffect(() => {
    let cancelled = false;
    const to = year === thisYear ? new Date().toISOString().slice(0, 10) : `${year}-12-31`;
    fetchClaimsReport(`${year}-01-01`, to, companyId ? Number(companyId) : undefined)
      .then((claims) => {
        if (!cancelled) setReport(claims);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Tableau de bord indisponible.");
      });
    return () => {
      cancelled = true;
    };
  }, [year, thisYear, companyId]);

  useEffect(() => {
    const range = providerRange(providerPeriod, year, new Date());
    const yearTo = year === thisYear ? new Date().toISOString().slice(0, 10) : `${year}-12-31`;
    if (range.from === `${year}-01-01` && range.to === yearTo) {
      setProviderRows(null);
      return;
    }
    let cancelled = false;
    fetchClaimsReport(range.from, range.to, companyId ? Number(companyId) : undefined)
      .then((claims) => {
        if (!cancelled) setProviderRows(claims.by_provider);
      })
      .catch(() => {
        if (!cancelled) setProviderRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [providerPeriod, year, thisYear, companyId]);

  const finance = dashboard?.finance;
  const key = report?.key_figures;

  return (
    <div className="flex flex-col gap-5">
      <DashboardBanner
        eyebrow="Tableau de bord décisionnel"
        title={insurerName}
        subtitle={`Portefeuille traité par SafeCheck${key ? ` · ${key.companies.length} entreprise(s)` : ""}${
          key ? ` · ${key.population.agents} agent(s) · ${key.population.spouses + key.population.children} ayant(s) droit` : ""
        }.`}
      />

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {!dashboard && !error ? <p className="text-sm text-muted">Chargement…</p> : null}

      {dashboard && finance ? (
        <>
          <InsurerKpiGrid
            dashboard={dashboard}
            received={dashboard.pipeline.find((stage) => stage.key === "received")}
            formatMoney={money}
            formatPercent={percent}
          />

          <InsurerPipeline pipeline={dashboard.pipeline} formatMoney={money} />

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <MonthlyEvolution months={dashboard.monthly} />

            {/* Notes de paiement : ce que le Sinistre transmet à régler pour les factures validées. */}
            <section className="theme-card flex flex-col p-5">
              <div className="mb-4 flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground" aria-hidden="true">
                  <Receipt size={19} strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-foreground">Notes de paiement</h2>
                  <p className="text-pretty text-xs text-muted-foreground">Montants à régler aux prestataires, transmis par le Sinistre après validation des factures.</p>
                </div>
              </div>

              <div className="mb-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-emerald-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#e3f8ec_100%)] p-3 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    <Inbox size={14} aria-hidden="true" />
                    Reçues
                  </p>
                  <p className="mt-1.5 text-2xl font-bold leading-none tabular-nums text-foreground">{dashboard.payment_notes.sent}</p>
                  <p className="mt-1 text-xs tabular-nums text-muted-foreground">{money(dashboard.payment_notes.sent_amount)} USD à payer</p>
                </div>
                <div className="rounded-xl border border-amber-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#fdf1d2_100%)] p-3 dark:border-amber-500/20 dark:bg-amber-500/10">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                    <Hourglass size={14} aria-hidden="true" />
                    En attente
                  </p>
                  <p className="mt-1.5 text-2xl font-bold leading-none tabular-nums text-foreground">{dashboard.payment_notes.pending}</p>
                  <p className="mt-1 text-xs tabular-nums text-muted-foreground">facture(s) sans note de paiement</p>
                </div>
              </div>

              <p className="mb-1 text-xs font-semibold text-muted-foreground">Dernières notes</p>
              {dashboard.payment_notes.latest.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-4 py-6 text-center">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-card-muted text-muted" aria-hidden="true">
                    <Receipt size={18} />
                  </span>
                  <p className="text-sm font-medium text-foreground">Aucune note de paiement</p>
                  <p className="text-pretty text-xs text-muted-foreground">Elles apparaissent ici dès qu'une facture est validée.</p>
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-border">
                  {dashboard.payment_notes.latest.map((note) => (
                    <li key={note.invoice_id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{note.hospital}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <span className="tabular-nums">Facture #{note.invoice_id}</span>
                          {note.sent_at ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                              Envoyée le {shortDate(note.sent_at)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                              Pas encore envoyée
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-sm font-semibold tabular-nums text-foreground">{money(note.to_pay)} USD</span>
                        <button
                          type="button"
                          onClick={() => void openPaymentNote(note.invoice_id, "insurer").catch((err) => setError(err instanceof Error ? err.message : "Note indisponible."))}
                          className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 text-xs font-medium text-foreground transition-colors hover:border-accent hover:bg-accent hover:text-accent-foreground"
                        >
                          <FileText size={12} aria-hidden="true" />
                          Voir la note
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <section className="theme-card relative p-5 transition-colors hover:border-accent xl:col-span-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-foreground">Consommation par entreprise</h2>
                {/* Le lien couvre toute la carte. */}
                <Link to="/dashboard/report/details/company" className="text-sm text-primary after:absolute after:inset-0 after:content-['']">Détail</Link>
              </div>
              <ul className="flex flex-col gap-1 text-sm">
                {dashboard.by_company.length === 0 ? <li className="text-muted">—</li> : null}
                {dashboard.by_company.map((row) => (
                  <li key={row.label} className="flex justify-between gap-2">
                    <span className="truncate text-foreground">
                      {row.label} <span className="text-xs text-muted">· {row.patients} patient(s)</span>
                    </span>
                    <span className="tabular-nums text-muted">{money(row.validated)}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="theme-card p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-foreground">Demandes de validation</h2>
                <Link to="/dashboard/requests" className="text-sm text-primary hover:underline">Voir toutes</Link>
              </div>
              {/* Mêmes libellés que la page des demandes ; chaque case y ouvre la liste filtrée. */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  { label: "En attente", value: dashboard.requests.pending, href: "/dashboard/requests?status=pending", alert: false },
                  { label: "Urgentes", value: dashboard.requests.urgent_pending, href: "/dashboard/requests?status=pending&urgency=urgent", alert: dashboard.requests.urgent_pending > 0 },
                  { label: "Approuvées", value: dashboard.requests.approved, href: "/dashboard/requests?status=approved", alert: false },
                  { label: "Rejetées", value: dashboard.requests.rejected, href: "/dashboard/requests?status=rejected", alert: false },
                ].map((tile) => (
                  <Link
                    key={tile.label}
                    to={tile.href}
                    className={`rounded-lg border p-2 transition-colors hover:border-accent hover:bg-accent/10 ${tile.alert ? "border-red-200 bg-red-50" : "border-border"}`}
                  >
                    <span className="block text-xs text-muted">{tile.label}</span>
                    <span className="block font-semibold tabular-nums text-foreground">{tile.value}</span>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          {report ? (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <TopList
                title="Natures d'acte les plus coûteuses"
                rows={report.by_nature}
                href="/dashboard/report/details/nature"
                companyFilter={{ id: "top-nature-company", value: companyId, companies, onChange: setCompanyId }}
              />
              <TopList
                title="Prestataires"
                rows={providerRows ?? report.by_provider}
                href="/dashboard/report/details/provider"
                periodFilter={{ id: "top-provider-period", value: providerPeriod, onChange: setProviderPeriod }}
              />
              <TopList
                title="Affections"
                rows={report.by_pathology}
                href="/dashboard/report/details/pathology"
                companyFilter={{ id: "top-pathology-company", value: companyId, companies, onChange: setCompanyId }}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
